import { computeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh';
import {
  BufferGeometry,
  Euler,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Raycaster,
  Vector3,
} from 'three';
import { createGltfLoader } from '@viewer/createGltfLoader';

BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
Mesh.prototype.raycast = acceleratedRaycast;

const GRAVITY = -12;
const PLAYER_EYE_HEIGHT = 1.6;
const PLAYER_RADIUS = 0.3;
const FLOOR_STEP_UP = 0.4;
const DIAG = Math.SQRT1_2;
const SWEEP_SKIN = 0.02;

export interface CollisionSceneTransform {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
}

export interface CollisionPhysicsState {
  playerVelocityY: number;
  isGrounded: boolean;
}

export interface HorizontalPoint {
  x: number;
  z: number;
}

export function limitHorizontalSweep(
  previous: HorizontalPoint,
  desired: HorizontalPoint,
  hitDistance: number,
  radius = PLAYER_RADIUS,
): HorizontalPoint | null {
  const dx = desired.x - previous.x;
  const dz = desired.z - previous.z;
  const dist = Math.hypot(dx, dz);
  if (dist <= 1e-6 || hitDistance > dist + radius) return null;
  const allowed = Math.max(0, hitDistance - radius - SWEEP_SKIN);
  const t = Math.min(1, allowed / dist);
  return {
    x: previous.x + dx * t,
    z: previous.z + dz * t,
  };
}

/**
 * Invisible BVH collision mesh used by Option 2 (PlayCanvas) for walk physics.
 * Option 1 keeps its inline collision path; both share the same constants.
 */
export class CollisionSystem {
  private readonly meshes: Object3D[] = [];
  private readonly floorRay = new Raycaster(new Vector3(), new Vector3(0, -1, 0), 0, PLAYER_EYE_HEIGHT + FLOOR_STEP_UP + 0.5);
  private readonly wallRaycaster = new Raycaster(new Vector3(), new Vector3(), 0, PLAYER_RADIUS);
  private readonly pickRaycaster = new Raycaster(new Vector3(), new Vector3());
  private readonly wallRayDirs = [
    new Vector3(1, 0, 0),
    new Vector3(-1, 0, 0),
    new Vector3(0, 0, 1),
    new Vector3(0, 0, -1),
    new Vector3(DIAG, 0, DIAG),
    new Vector3(DIAG, 0, -DIAG),
    new Vector3(-DIAG, 0, DIAG),
    new Vector3(-DIAG, 0, -DIAG),
  ];
  private readonly floorDown = new Vector3(0, -1, 0);
  private readonly wallOrigin = new Vector3();
  private readonly wallPush = new Vector3();
  private readonly sweepOrigin = new Vector3();
  private readonly sweepDir = new Vector3();
  private readonly floorHits: ReturnType<Raycaster['intersectObjects']> = [];
  private readonly wallHits: ReturnType<Raycaster['intersectObjects']> = [];
  private loaded = false;
  private physics: CollisionPhysicsState = { playerVelocityY: 0, isGrounded: false };

  constructor() {
    (this.floorRay as Raycaster & { firstHitOnly?: boolean }).firstHitOnly = true;
    (this.wallRaycaster as Raycaster & { firstHitOnly?: boolean }).firstHitOnly = true;
  }

  get isLoaded(): boolean {
    return this.loaded;
  }

  getPhysicsState(): CollisionPhysicsState {
    return this.physics;
  }

  resetPhysics(): void {
    this.physics.playerVelocityY = 0;
    this.physics.isGrounded = false;
  }

  async load(url: string, transform: CollisionSceneTransform): Promise<boolean> {
    try {
      const loader = createGltfLoader();
      const gltf = await loader.loadAsync(url);
      const euler = new Euler(
        transform.rotation[0] * (Math.PI / 180),
        transform.rotation[1] * (Math.PI / 180),
        transform.rotation[2] * (Math.PI / 180),
        'XYZ',
      );
      gltf.scene.position.set(...transform.position);
      gltf.scene.quaternion.setFromEuler(euler);
      gltf.scene.scale.set(...transform.scale);
      gltf.scene.updateMatrixWorld(true);

      const invisibleMat = new MeshBasicMaterial({ visible: false });
      gltf.scene.traverse((child) => {
        if ((child as Mesh).isMesh) {
          const mesh = child as Mesh;
          mesh.material = invisibleMat;
          (mesh.geometry as BufferGeometry).computeBoundsTree?.();
          this.meshes.push(mesh);
        }
      });

      this.loaded = this.meshes.length > 0;
      return this.loaded;
    } catch (error) {
      this.loaded = false;
      console.warn('[GII] Collision mesh unavailable; interaction remains locked.', {
        url,
        error,
      });
      return false;
    }
  }

  /** Apply gravity + floor snap + wall push-back. Mutates `position` in place. */
  applyPhysics(
    position: Vector3,
    deltaSeconds: number,
    skipCollision = false,
    previousPosition?: Readonly<{ x: number; y: number; z: number }>,
  ): void {
    if (skipCollision || this.meshes.length === 0) return;

    if (previousPosition) {
      const dx = position.x - previousPosition.x;
      const dz = position.z - previousPosition.z;
      const dist = Math.hypot(dx, dz);
      if (dist > 1e-6) {
        const midY = previousPosition.y - PLAYER_EYE_HEIGHT * 0.5;
        this.sweepOrigin.set(previousPosition.x, midY, previousPosition.z);
        this.sweepDir.set(dx / dist, 0, dz / dist);
        this.wallRaycaster.set(this.sweepOrigin, this.sweepDir);
        this.wallRaycaster.far = dist + PLAYER_RADIUS;
        this.wallHits.length = 0;
        const hits = this.wallRaycaster.intersectObjects(this.meshes, true, this.wallHits);
        if (hits.length > 0) {
          const limited = limitHorizontalSweep(previousPosition, position, hits[0]!.distance);
          if (limited) {
            position.x = limited.x;
            position.z = limited.z;
          }
        }
        this.wallRaycaster.far = PLAYER_RADIUS;
      }
    }

    if (!this.physics.isGrounded) {
      this.physics.playerVelocityY += GRAVITY * deltaSeconds;
      position.y += this.physics.playerVelocityY * deltaSeconds;
    }

    this.floorRay.set(position, this.floorDown);
    this.floorHits.length = 0;
    const floorHits = this.floorRay.intersectObjects(this.meshes, true, this.floorHits);
    if (floorHits.length > 0) {
      const groundY = floorHits[0]!.point.y + PLAYER_EYE_HEIGHT;
      if (position.y <= groundY) {
        position.y = groundY;
        this.physics.playerVelocityY = 0;
        this.physics.isGrounded = true;
      } else {
        this.physics.isGrounded = false;
      }
    } else {
      this.physics.isGrounded = false;
    }

    const midY = position.y - PLAYER_EYE_HEIGHT * 0.5;
    this.wallOrigin.set(position.x, midY, position.z);
    for (const dir of this.wallRayDirs) {
      this.wallRaycaster.set(this.wallOrigin, dir);
      this.wallHits.length = 0;
      const hits = this.wallRaycaster.intersectObjects(this.meshes, true, this.wallHits);
      if (hits.length > 0) {
        const push = PLAYER_RADIUS - hits[0]!.distance;
        position.addScaledVector(this.wallPush.copy(dir).negate(), push);
      }
    }
  }

  /** Closest hit distance along a ray against collision geometry, or null if none within maxDistance. */
  raycastDistance(
    origin: Readonly<{ x: number; y: number; z: number }>,
    direction: Readonly<{ x: number; y: number; z: number }>,
    maxDistance: number,
  ): number | null {
    if (!this.loaded || this.meshes.length === 0 || maxDistance <= 0) return null;

    const o = new Vector3(origin.x, origin.y, origin.z);
    const d = new Vector3(direction.x, direction.y, direction.z);
    if (d.lengthSq() < 1e-12) return null;
    d.normalize();

    this.pickRaycaster.set(o, d);
    this.pickRaycaster.near = 0.01;
    this.pickRaycaster.far = maxDistance;
    this.wallHits.length = 0;
    const hits = this.pickRaycaster.intersectObjects(this.meshes, true, this.wallHits);
    if (hits.length === 0) return null;
    return hits[0]!.distance;
  }

  dispose(): void {
    this.meshes.length = 0;
    this.loaded = false;
    this.resetPhysics();
  }
}

export const COLLISION_MOVE_SPEED = 3.5;
export const COLLISION_SPRINT_SPEED = 8;
export const COLLISION_SLOW_SPEED = 1.2;
